import { useLazyQuery } from '@apollo/client'
import { useEffect, useMemo } from 'react'
import { useUpdateEffect } from 'react-use'

import { gql } from '../../../__generated__/graphql/catalog'
import { reportClientError } from '../../../app/_components/chrome/scripts/DataDogRumScript'
import { NostoPlacementId } from '../../../app/_components/chrome/scripts/NostoScript'
import { derivePricingForVariantGroup } from '../../../app/_components/pricing/PricingRange.client'
import { useFetchNostoPlacementData } from '../../../app/_hooks/useFetchNostoPlacementData'
import { QueryStatus } from '../types'

export function useNostoRecos({
  nostoPlacementId,
  productCountLimit = 24,
  skip,
}: {
  nostoPlacementId: NostoPlacementId
  productCountLimit?: number
  skip?: boolean
}) {
  /*
    We purposefully do not fetch much on the variant level. Nosto recommends Shopify products, not variants.
    So we want to focus on the variantGroup related to that variant and its data.
   */
  const [fetchRecsData, { data, error, called, loading }] = useLazyQuery(
    gql(`
      query GetDataForNostoRecs($skusForVariantGroups: [String!]) {
        productCatalogVariants(skus: $skusForVariantGroups) {
          __typename
          id
          urlPath
          shopifyProductId
          fit {
            id
            displayValue
          }
          group {
            __typename
            id
            title
            exclusive
            noSearchIndex
            availability
            color {
              id
              displayValue
            }
            priceRange {
              min {
                amount
              }
              max {
                amount
              }
              currencyCode
            }
            images(first: 1) {
              url
            }
            product {
              __typename
              id
              handle
              options {
                colorsTotalCount
              }
            }
          }
        }
      }
    `)
  )

  useUpdateEffect(() => {
    if (!error) return
    reportClientError({
      error,
      context: {},
    })
  }, [error])

  const {
    data: { skusForVariantGroups, title: placementTitle, resultId: placementResultId },
    status: nostoStatus,
  } = useFetchNostoPlacementData(nostoPlacementId, {
    skip: skip ?? false,
  })

  useEffect(() => {
    if (!skip && skusForVariantGroups && skusForVariantGroups.length > 0) {
      fetchRecsData({
        variables: { skusForVariantGroups: skusForVariantGroups.slice(0, productCountLimit) },
      })
    }
  }, [fetchRecsData, productCountLimit, skip, skusForVariantGroups])

  const productCatalogVariants = data?.productCatalogVariants
  const validVariants = useMemo(() => {
    if (error) return []
    return (productCatalogVariants ?? [])
      ?.filter(v => !v.group.noSearchIndex && v.group.availability === 'AVAILABLE')
      .map(v => {
        return {
          ...v,
          pricing: derivePricingForVariantGroup(v.group),
        }
      })
  }, [error, productCatalogVariants])

  const status = useMemo<Exclude<QueryStatus, 'idle'>>(() => {
    if (skip) return 'resolved'

    // We are waiting on Nosto JS
    if (nostoStatus === 'pending') return 'pending'

    // Failed
    if (error || nostoStatus === 'rejected') return 'rejected'

    // Notso came back with nothing for us
    if (!skusForVariantGroups || skusForVariantGroups.length === 0) return 'resolved'

    // Waiting on product details query
    if (!called || loading) return 'pending'

    return 'resolved'
  }, [called, error, loading, nostoStatus, skip, skusForVariantGroups])

  return {
    error,
    placementTitle,
    variants: validVariants,
    resultId: placementResultId,
    status,
  }
}
